"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { api } from "../../services/Axios"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../services/AuthContext"
import { Mail, User, ArrowRight, LogIn, KeyRound } from "lucide-react"
import Carousel from "../utils/carousel/Carousel"
import Loader from "../utils/loader/Loader"
import Notification from "../utils/avisos/Notification"
import Input from "../ui/Input"
import Button from "../ui/Button"
import PasswordInput from "../ui/PasswordInput"

const images = ["/carrusel/view1.png", "/carrusel/view2.png", "/carrusel/view3.png"]

const LoginRegister = () => {
  const { syncAuthStatus, isAuthenticated, userData } = useAuth()
  const [view, setView] = useState<'login' | 'register' | 'forgot-password'>('login');
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [name, setName] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [formAnimation, setFormAnimation] = useState("fade-in")
  const [redirectReady, setRedirectReady] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState("")
  const [notificationType, setNotificationType] = useState<"error" | "success" | "info">("error")
  const navigate = useNavigate()

  useEffect(() => {
    setNotificationMessage("")
  }, [view])

  const handleToggle = (targetView: 'login' | 'register' | 'forgot-password') => {
    setFormAnimation("fade-out")
    setTimeout(() => {
      setView(targetView)
      setFormAnimation("fade-in")
      if (view === 'register' || targetView === 'register') {
        setEmail("")
      }
      setPassword("")
      setName("")
      setNotificationMessage("")
    }, 300)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setNotificationType("error");
      setNotificationMessage("Por favor completa todos los campos.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.post("/api/auth/login", { email, password });
      localStorage.setItem("token", response.data.token);
      await syncAuthStatus();
      setRedirectReady(true);
      setNotificationType("success");
      setNotificationMessage("Inicio de sesión exitoso");
    } catch (err: any) {
      if (err.response?.status === 403) {
        setNotificationType("info");
        setNotificationMessage("Por favor verifica tu correo. Se ha enviado un nuevo código.");
        localStorage.setItem("email", email);
        navigate("/verificacion");
      } else {
        setNotificationType("error");
        setNotificationMessage(err.response?.data?.msg || "Credenciales incorrectas.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (isAuthenticated && userData && redirectReady) {
      const redirectPath = userData.role === "vendedor" ? "/caja-venta" : "/";
      navigate(redirectPath);
    }
  }, [isAuthenticated, userData, redirectReady, navigate]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !password) {
        setNotificationType("error");
        setNotificationMessage("Por favor completa todos los campos.");
        return;
    }
    setIsLoading(true);
    try {
      await api.post("/api/auth/register", { nombre: name, email, password });
      setNotificationType("success");
      setNotificationMessage("Registro exitoso. Revisa tu correo para verificar tu cuenta.");
      localStorage.setItem("email", email);
      navigate("/verificacion");
    } catch (err: any) {
      setNotificationType("error");
      setNotificationMessage(err.response?.data?.msg || "Error en el registro.");
    } finally {
      setIsLoading(false);
    }
  }
  
  const handleForgotPasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setNotificationType("error");
      setNotificationMessage("Por favor, ingresa tu correo electrónico.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.post("/api/auth/forgot-password", { email });
      setNotificationType("success");
      setNotificationMessage(response.data.msg);
    } catch (err: any) {
      setNotificationType("error");
      setNotificationMessage(err.response?.data?.msg || "Ocurrió un error.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- FUNCIÓN CORREGIDA ---
  const renderFormContent = () => {
    switch(view) {
      case 'forgot-password':
        return (
          // Envoltura con Fragmento <> ... </>
          <>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Recuperar Contraseña</h2>
            <p className="text-gray-500 text-sm mb-6">Ingresa tu correo y te enviaremos un enlace.</p>
            <form onSubmit={handleForgotPasswordRequest} className="space-y-5" autoComplete="off">
              <Input
                label="Correo Electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Indica tu correo electrónico"
                fullWidth
                icon={<Mail className="h-5 w-5 text-gray-400" />}
                autoComplete="email"
              />
              <Button type="submit" fullWidth size="lg">
                <span className="flex items-center justify-center">
                  <KeyRound className="mr-2 h-5 w-5" />
                  Enviar Enlace
                </span>
              </Button>
            </form>
          </>
        );
      case 'register':
        return (
          // Envoltura con Fragmento <> ... </>
          <>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Crea una cuenta</h2>
            <form onSubmit={handleRegister} className="space-y-5" autoComplete="off">
              <Input
                label="Nombre Completo"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Escribe tu nombre completo"
                fullWidth
                icon={<User className="h-5 w-5 text-gray-400" />}
                autoComplete="name"
              />
              <Input
                label="Correo Electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Indica tu correo electrónico"
                fullWidth
                icon={<Mail className="h-5 w-5 text-gray-400" />}
                autoComplete="email"
              />
              <PasswordInput
                label="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Escribe tu contraseña"
                fullWidth
                showStrengthIndicator={true}
                autoComplete="new-password"
              />
              <Button type="submit" fullWidth size="lg">
                <span className="flex items-center justify-center">
                  <ArrowRight className="mr-2 h-5 w-5" />
                  Registrarse
                </span>
              </Button>
            </form>
          </>
        );
      case 'login':
      default:
        return (
          // Envoltura con Fragmento <> ... </>
          <>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Ingresa a tu cuenta</h2>
            <form onSubmit={handleLogin} className="space-y-5">
              <Input
                label="Correo Electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Indica tu correo electrónico"
                fullWidth
                icon={<Mail className="h-5 w-5 text-gray-400" />}
                autoComplete="username" 
              />
              <PasswordInput
                label="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Escribe tu contraseña"
                fullWidth
                autoComplete="current-password"
              />
              <div className="text-right text-sm">
                <button
                  type="button"
                  onClick={() => handleToggle('forgot-password')}
                  className="font-medium text-primary hover:text-primary/80"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <Button type="submit" fullWidth size="lg">
                <span className="flex items-center justify-center">
                  <LogIn className="mr-2 h-5 w-5" />
                  Ingresar
                </span>
              </Button>
            </form>
          </>
        );
    }
  };

  const renderToggleText = () => {
    switch(view) {
        case 'forgot-password':
            return (
                <button type="button" onClick={() => handleToggle('login')} className="w-full py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    ¿Ya recordaste? Inicia sesión
                </button>
            );
        case 'register':
            return (
                <button type="button" onClick={() => handleToggle('login')} className="w-full py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    ¿Ya tienes cuenta? Inicia sesión
                </button>
            );
        case 'login':
        default:
            return (
                <button type="button" onClick={() => handleToggle('register')} className="w-full py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    ¿No tienes cuenta? Regístrate
                </button>
            );
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {isLoading && <Loader />}
      {notificationMessage && (
        <Notification
          message={notificationMessage}
          type={notificationType}
          onClose={() => setNotificationMessage("")}
        />
      )}
      <div className="hidden md:block md:w-1/2 h-full relative overflow-hidden bg-primary">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary/30 rounded-full blur-3xl"></div>
        <div className="absolute inset-0 z-10">
          <Carousel images={images} interval={6000} className="h-full" />
        </div>
      </div>
      <div className="w-full md:w-1/2 h-full overflow-auto bg-gray-50 flex flex-col">
        <div className="flex-1 flex flex-col justify-center items-center p-6 md:p-12">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <img src="/logos/logof.png" alt="Logo" className="h-20 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Bienvenido a ColStock</h1>
              <p className="text-gray-600">Sistema de gestión de inventario y ventas</p>
            </div>
            <div
              className={`w-full transition-all duration-300 ${formAnimation === "fade-in" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            >
              {/* Esta estructura ahora es correcta. El div de abajo es el único hijo directo */}
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                {renderFormContent()}

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">o</span>
                  </div>
                </div>

                {renderToggleText()}
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 text-center">
          <p className="text-gray-500 text-sm">
            Creado por{" "}
            <a href="https://vodac.co/" target="_blank" rel="noopener noreferrer" className="text-primary font-bold hover:underline">
              Vodac
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginRegister;